import type {ReactElement} from 'react';
import {useContext} from 'react';
import {StyleTile} from '@Components/style-tile/style-tile';
import styles from '@Components/mystuff-events-home/components/page-details-section/page-details-section.module.scss';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {StylesInputLeftDrawer} from '@Components/styles-input-left-drawer/styles-input-left-drawer';
import {useDispatch} from 'react-redux';
import {getEventWizardStyleColor, getEventWizardStyleFont, useEventWizardIsStylesPanelOpen, useEventWizardStyles} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardIsStylesPanelOpenUpdate, onEventWizardStyleColorUpdate, onEventWizardStyleFontUpdate} from '@Components/event-wizard/event-wizard-slice';
import {updateStyleColor, updateStyleFont} from '@Components/event-wizard/preview/preview.helper';
import {getNewStyleColors} from '@Components/event-wizard/event-style/event-style.helper';
import {Text, TextSize} from '@Components/text';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {openStylesInputModal} from '@Modals/styles-input-modal/styles-input-modal.library';
import {GA4EventName} from "@Libraries/ga-events";
import type {GAEventsMap} from "@Panels/styles-input-panel/styles-input-panel.types";
import { StylesInputPanelGAEventType} from "@Panels/styles-input-panel/styles-input-panel.types";

export function EventStyle(): ReactElement<any> {
  const dispatch = useDispatch();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const selectedStyle = useEventWizardStyles();
  const isStylesPanelOpen = useEventWizardIsStylesPanelOpen();

  const openStylesPanel = (): void => {
    if (isSmallScreen) {
      openStylesInputModal({
        allowImageSelection: false,
        selectedColorHex: selectedStyle.colors.primary,
        selectedFontFamily: selectedStyle.fontFamily,
        getColorReduxSelector: getEventWizardStyleColor,
        getFontReduxSelector: getEventWizardStyleFont,
        onSelectColorHex,
        onSelectFontFamily,
      });
      return;
    }
    dispatch(onEventWizardIsStylesPanelOpenUpdate(true));
  };

  const closeStylesPanel = (): void => {
    dispatch(onEventWizardIsStylesPanelOpenUpdate(false));
  };

  const onSelectColorHex = (newColor: string): void => {
    const newColors = getNewStyleColors(newColor);
    dispatch(onEventWizardStyleColorUpdate(newColors));
    updateStyleColor(newColors);
  };

  const onSelectFontFamily = (newFont: string): void => {
    dispatch(onEventWizardStyleFontUpdate(newFont));
    updateStyleFont(newFont);
  };

  const getGA4EventsMap = (): GAEventsMap => {
    return {
      [StylesInputPanelGAEventType.BRAND_KIT]: GA4EventName.EVENTS_APPLY_BRAND_KIT,
      [StylesInputPanelGAEventType.COLOR]: GA4EventName.EVENT_WIZARD_APPLY_COLOR,
      [StylesInputPanelGAEventType.FONT]: GA4EventName.EVENT_WIZARD_APPLY_FONT,
    }
  }

  return (
    <div>
      <div className="flex-row-align-center spacing-m-b-1">
        <Text val={window.i18next.t('pmwjs_styles')} size={TextSize.XXSMALL} bold />
      </div>
      <StyleTile
        tileText={window.i18next.t('pmwjs_default')}
        className={styles.styleTile}
        isLoading={false}
        showImage={false}
        colorHexes={[selectedStyle.colors.primary, PMW_COLORS_NEUTRAL.NEUTRAL_0, PMW_COLORS_NEUTRAL.NEUTRAL_70]}
        fontFamily={selectedStyle.fontFamily}
        onClick={openStylesPanel}
      />

      <StylesInputLeftDrawer
        isOpen={isStylesPanelOpen}
        allowImageSelection={false}
        selectedColorHex={selectedStyle.colors.primary}
        onSelectColorHex={onSelectColorHex}
        selectedFontFamily={selectedStyle.fontFamily}
        onSelectFontFamily={onSelectFontFamily}
        onClose={closeStylesPanel}
        gaEventsMap={getGA4EventsMap()}
      />
    </div>
  );
}
